import React, { forwardRef, HTMLAttributes } from "react";
import { cn } from "./Utils.js";

// Generic type for all Card components
type CardProps = HTMLAttributes<HTMLDivElement>;

// Card
const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("bg-card text-card-foreground rounded-lg", className)}
      {...props}
    />
  ),
);
Card.displayName = "Card";

// CardHeader
const CardHeader = forwardRef<HTMLDivElement, CardProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex flex-col space-y-1.5 px-4 py-2", className)}
      {...props}
    />
  ),
);
CardHeader.displayName = "CardHeader";

// CardTitle
const CardTitle = forwardRef<HTMLHeadingElement, CardProps>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn(
        "text-2xl font-semibold leading-none tracking-tight",
        className,
      )}
      {...props}
    />
  ),
);
CardTitle.displayName = "CardTitle";

// CardDescription
const CardDescription = forwardRef<HTMLParagraphElement, CardProps>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  ),
);
CardDescription.displayName = "CardDescription";

// CardContent
const CardContent = forwardRef<HTMLDivElement, CardProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-0 pt-0", className)} {...props} />
  ),
);
CardContent.displayName = "CardContent";

// CardFooter
const CardFooter = forwardRef<HTMLDivElement, CardProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex items-center p-0 pt-0", className)}
      {...props}
    />
  ),
);
CardFooter.displayName = "CardFooter";

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
};
